import {StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {Text, View} from "@khanacademy/wonder-blocks-core";
import {ModalLauncher, OnePaneDialog} from "@khanacademy/wonder-blocks-modal";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelMedium} from "@khanacademy/wonder-blocks-typography";
import {doNotTranslateYet, NoTransYet} from "@khan/i18n";
import {mediaQueries} from "@khan/shared-styles";

type Props = ControlProps & {
    // The name of the file to delete.
    filename: string;

    // Called when the user confirms the deletion.
    onConfirm: () => void;
};

// Props used to determine if the modal is open. Either some children that can
// open the modal, or a boolean and close callback for a controlled modal.
type ControlProps =
    | {
          children: (props: {openModal: () => unknown}) => React.ReactNode;
      }
    | {
          opened: boolean;
          onClose: () => void;
      };

export default function DeleteFileModalLauncher({
    filename,
    onConfirm,
    ...restProps
}: Props) {
    return (
        <ModalLauncher
            {...restProps}
            modal={({closeModal}) => (
                <OnePaneDialog
                    style={styles.dialog}
                    title={doNotTranslateYet`Delete file?`}
                    content={
                        <View style={styles.content}>
                            <LabelMedium>
                                <NoTransYet>
                                    Are you sure you want to delete{" "}
                                    <Text tag="b">{filename}</Text>?
                                </NoTransYet>
                            </LabelMedium>
                            <LabelMedium>
                                {doNotTranslateYet`You can’t undo this action. Make sure to update all references to this file, including any import statements.`}
                            </LabelMedium>
                        </View>
                    }
                    footer={
                        <View style={styles.footer}>
                            <Button kind="tertiary" onClick={closeModal}>
                                {doNotTranslateYet`Cancel`}
                            </Button>
                            <Button
                                actionType="destructive"
                                onClick={() => {
                                    onConfirm();
                                    closeModal();
                                }}
                            >
                                {doNotTranslateYet`Delete`}
                            </Button>
                        </View>
                    }
                />
            )}
        />
    );
}

const styles = StyleSheet.create({
    dialog: {
        maxWidth: 617,
        maxHeight: 290,
        [mediaQueries.smOrSmaller]: {
            maxHeight: 380,
        },
    },
    content: {
        display: "flex",
        flexDirection: "column",
        gap: spacing.medium_16,
    },
    footer: {
        display: "flex",
        flexDirection: "row",
        gap: spacing.medium_16,
    },
});
